import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProgressBar } from '@insio/ui-toolkit';
import { CreditUsageLevel } from '@insio/shared-types';
import type { CreditUsageFieldsFragment } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useCreditBillingPaths } from '../hooks/use-credit-billing-paths';
import { formatCreditPeriodEnd } from '../utils/credit-usage-view';

interface CreditUsageSummaryProps {
  /** The payer's usage; renders nothing without figures. */
  usage: CreditUsageFieldsFragment;
}

const BAR_VARIANT = {
  [CreditUsageLevel.OK]: 'success',
  [CreditUsageLevel.LOW]: 'warn',
  [CreditUsageLevel.EXHAUSTED]: 'error'
} as const;

/** The payer's credits in the billing table: the period's usage, extra credits, renewal and statement link. */
export function CreditUsageSummary({ usage }: CreditUsageSummaryProps) {
  const t = useFeatureTranslation('credits.billing');
  const { i18n } = useTranslation();
  const paths = useCreditBillingPaths();

  const figures = usage.figures;
  if (!figures) return null;

  const formatNumber = (value: number) => value.toLocaleString(i18n.language);
  const extra = figures.packRemaining + figures.manualRemaining;
  const hasPeriod = figures.allotment !== null;

  let periodLabel = t('noPeriod');
  if (figures.periodEnd) {
    const date = formatCreditPeriodEnd(figures.periodEnd, i18n.language);
    periodLabel = figures.renews ? t('renewsOn', { date }) : t('endsOn', { date });
  }

  const usageLabel = hasPeriod
    ? t('usage', {
        used: formatNumber(figures.usedInPeriod ?? 0),
        allotment: formatNumber(figures.allotment ?? 0)
      })
    : t('available', { count: figures.balance, credits: formatNumber(figures.balance) });

  return (
    <div className='flex w-full flex-col gap-xs' data-testid='credit-usage-summary'>
      <div className='flex flex-wrap items-baseline justify-between gap-x-md gap-y-2xs'>
        <div className='flex flex-wrap items-baseline gap-x-sm gap-y-2xs'>
          <span className='text-sm font-semibold tabular-nums whitespace-nowrap text-foreground-base'>
            {usageLabel}
            {hasPeriod && figures.usedPercent !== null && (
              <span className='ml-xs font-normal text-foreground-muted'>
                ({t('usedPercent', { percent: figures.usedPercent })})
              </span>
            )}
          </span>
          {hasPeriod && extra > 0 && (
            <span className='text-xs font-medium tabular-nums whitespace-nowrap text-foreground-base'>
              {t('extra', { credits: formatNumber(extra) })}
            </span>
          )}
        </div>
        <Link
          to={paths.transactions}
          className='text-sm font-medium text-foreground-base underline-offset-2 hover:underline'
        >
          {t('transactionsLink')}
        </Link>
      </div>

      {hasPeriod && (
        <ProgressBar
          value={figures.usedPercent ?? 0}
          variant={BAR_VARIANT[figures.level]}
          ariaLabel={t('progressAriaLabel', { label: usageLabel })}
        />
      )}

      <span className='text-xs text-foreground-muted'>{periodLabel}</span>
    </div>
  );
}
